<script setup>
/**
 * "Try it before customers do" (P-2.11): Test it in a drawer, on the task exactly as the Check
 * screen has it, saved or not, on or off.
 *
 * It is the same rehearsal as the Test it page (`useTestSession`): the same price on every
 * message, the same once-per-session acknowledgement (O-11), the same thread, trace and customer
 * card. What rides along is the draft (`rehearse`): re-sent on every message and never stored.
 * The first message can start the task straight away (`startNow`), which skips the assistant's
 * first read; the owner can untick that to test how customers reach it.
 *
 * Real actions stay off here: a rehearsal of a draft must never book or charge anything. Live
 * lists use the sample the owner's system sent when its connection was last tested.
 */
import { computed, nextTick, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

import AiConfirmDialog from '../AiConfirmDialog.vue';
import TestCustomerCard from '../TestCustomerCard.vue';
import TestThread from '../TestThread.vue';
import WasloDrawer from '../WasloDrawer.vue';
import WasloTextArea from '../WasloTextArea.vue';
import { useWasloI18n } from '../../i18n.js';
import { useWasloAccountId } from '../../composables/useWasloAccountId.js';
import { useTestSession } from '../../composables/useTestSession.js';
import {
  cardAfterReply,
  contactPayloadOf,
  startingCard,
} from '../../helpers/customer-card.js';
import { DEFAULT_TEST_CHANNEL } from '../../helpers/test-channel.js';

const props = defineProps({
  /** The stored task's id, or `null` for a task not saved yet. */
  taskId: { type: Number, default: null },
  /** The compiled draft (`definitionOf`), as the Check screen has it. */
  definition: { type: Object, default: null },
  title: { type: String, default: '' },
  trigger: { type: String, default: '' },
  /** Where the overlay is teleported. A test may point it somewhere it can read. */
  to: { type: String, default: 'body' },
});

/** `tried`: a message was answered, so the Check screen re-reads "Tried …". */
const emit = defineEmits(['tried']);

const { t } = useWasloI18n();
const accountId = useWasloAccountId();
const key = name => `WASLO.TASKS.COMPOSE.CHECK.${name}`;

const drawer = ref(null);
const draft = ref('');
const composerMessage = ref('');
const startNow = ref(true);

const card = ref(startingCard(DEFAULT_TEST_CHANNEL));
const cardChanged = ref([]);
const cardRefused = ref([]);

const session = useTestSession({
  accountId,
  extras: ({ first }) => {
    const contact = contactPayloadOf(card.value);
    return {
      liveActions: false,
      channel: DEFAULT_TEST_CHANNEL,
      ...(contact ? { contact } : {}),
      rehearse: {
        ...(props.taskId === null ? {} : { taskId: props.taskId }),
        definition: props.definition,
        title: props.title.trim(),
        trigger: props.trigger.trim(),
        ...(first && startNow.value ? { startNow: true } : {}),
      },
    };
  },
  onReply: reply => {
    const next = cardAfterReply(card.value, reply?.trace?.customer ?? null);
    card.value = next.card;
    cardChanged.value = next.changed;
    cardRefused.value = next.refused;
    emit('tried');
  },
});
const { charge, turns, isSending, sendLabel, receiptText, sampleNote } =
  session;

const cardAnswers = computed(() => {
  const found = [...turns.value]
    .reverse()
    .find(turn => turn.trace?.stepTask?.answers?.length);
  return found ? found.trace.stepTask.answers : [];
});

const resetCard = () => {
  card.value = startingCard(DEFAULT_TEST_CHANNEL);
  cardChanged.value = [];
  cardRefused.value = [];
};

/** A fresh chat and a fresh customer every time it opens: each try is its own rehearsal. */
const open = async () => {
  session.restart();
  resetCard();
  draft.value = '';
  composerMessage.value = '';
  startNow.value = true;
  await nextTick();
  drawer.value?.open();
};

const send = async () => {
  if (isSending.value) return;
  const message = draft.value.trim();
  composerMessage.value = '';
  if (!message) {
    composerMessage.value = t('WASLO.TEST.COMPOSER.EMPTY');
    return;
  }
  draft.value = '';
  if ((await session.sendTurn(message, null)) === 'cancelled') {
    draft.value = message;
  }
};

const choose = async option => {
  if (isSending.value) return;
  await session.sendTurn(option.title, {
    id: option.choiceId,
    title: option.title,
  });
};

const say = async text => {
  if (isSending.value) return;
  await session.sendTurn(text, null);
};

/** Enter sends; Shift and Enter start a new line; an IME candidate is left alone. */
const onComposerKeydown = event => {
  if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
  event.preventDefault();
  send();
};

defineExpose({ open });
</script>

<template>
  <WasloDrawer
    ref="drawer"
    placement="end"
    overflow-y-auto
    :to="to"
    :title="t(key('TRY_HEADING'))"
    :description="t(key('TRY_NOTE'))"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <!-- The drawer's root is a Teleport, so the marker sits on what it holds. -->
    <div
      class="flex flex-col gap-4"
      data-waslo-rehearsal
      data-waslo-rehearsal-body
    >
      <label
        v-if="!turns.length"
        class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-12"
      >
        <Checkbox v-model="startNow" data-waslo-rehearsal-start-now />
        <span>{{ t(key('TRY_START_NOW')) }}</span>
      </label>

      <TestThread
        :turns="turns"
        :busy="isSending"
        @choose="choose"
        @say="say"
        @fetch-sample="session.fetchSample"
      />
      <p
        v-if="sampleNote"
        class="mb-0 text-sm text-n-slate-11"
        role="status"
        data-waslo-rehearsal-sample-note
      >
        {{ sampleNote }}
      </p>

      <div class="flex items-end gap-3">
        <WasloTextArea
          v-model="draft"
          class="flex-1"
          :label="t('WASLO.TEST.COMPOSER.LABEL')"
          :placeholder="t('WASLO.TEST.COMPOSER.PLACEHOLDER')"
          :disabled="isSending"
          min-height="2.25rem"
          max-height="8rem"
          data-waslo-rehearsal-composer
          @keydown="onComposerKeydown"
        />
        <Button
          type="button"
          :label="sendLabel"
          variant="solid"
          color="blue"
          :is-loading="isSending"
          :disabled="isSending"
          data-waslo-rehearsal-send
          @click="send"
        />
      </div>
      <p v-if="composerMessage" class="mb-0 text-sm text-n-ruby-9" role="alert">
        {{ composerMessage }}
      </p>
      <p v-if="receiptText" class="mb-0 text-xs text-n-slate-11">
        {{ receiptText }}
      </p>

      <TestCustomerCard
        :card="card"
        :changed="cardChanged"
        :refused="cardRefused"
        :answers="cardAnswers"
        @update:card="value => (card = value)"
        @reset="resetCard"
      />
      <AiConfirmDialog :controller="charge" />
    </div>
  </WasloDrawer>
</template>
